import { ClassValue } from 'clsx';
import { useEffect } from 'react';
import { Button, Heading, Modal, Text } from '@/components/base';
import Icon, { IconName } from '@/components/base/Icon';
import { ErrorLogData } from '@/interfaces/logging';
import { toClassNames } from '@/utils';
import { getCurrentSessionInfo } from '@/utils/activitySession';
import { logger } from '@/utils/logger';

interface ErrorModalProps {
  open: boolean;
  title: string;
  description: string;
  buttonText: string;
  secondaryButtonText?: string;
  onButtonClick: () => void;
  onSecondaryButtonClick?: () => void;
  icon?: IconName;
  iconClassName?: ClassValue;
  errorLogData?: ErrorLogData;
}

const ErrorModal = ({
  open,
  title,
  description,
  buttonText,
  secondaryButtonText,
  onButtonClick,
  onSecondaryButtonClick,
  icon,
  iconClassName,
  errorLogData,
}: ErrorModalProps) => {
  const iconifyAlertClass = 'icon-[lucide--triangle-alert]';
  const { location } = window;
  const pageView = location.pathname + location.search;

  const currentSession = getCurrentSessionInfo();

  // Log error when modal opens
  useEffect(() => {
    if (open) {
      const { action, component = 'ErrorModal', description: errorDescription, context, error } = errorLogData || {};

      const logPayload = {
        component: errorLogData?.component ?? 'ErrorModal',
        pageView,
        modalDescription: description,
        action,
        description: errorDescription,
        context,
        user: { id: currentSession?.userId },
        sessionId: currentSession?.sessionId,
        title,
      };
      const logMessage = `Error displayed in ${component}: ${title}`;

      // Use the error from errorLogData if available, otherwise log as context
      if (error instanceof Error) {
        logger.error(logMessage, error, logPayload);
      } else {
        logger.error(logMessage, undefined, { ...logPayload, error });
      }
    }
  }, [open, pageView, errorLogData, currentSession?.userId, title, description, currentSession?.sessionId]);

  return (
    <Modal open={open} size="sm" dialogClassname="z-[60]">
      <div className="flex flex-col items-center justify-center">
        {icon ? (
          <Icon icon={icon} className={toClassNames('mr-2 text-error-500', iconClassName)} />
        ) : (
          <span className={toClassNames('mr-2 text-error-500', iconifyAlertClass, iconClassName)}></span>
        )}

        <Heading variant="h5" className="my-2">
          {title}
        </Heading>
        <Text variant="xs" className="mb-4 px-4 text-center text-gray-500">
          {description}
        </Text>

        <div className="mb-4 mt-3 flex gap-6">
          {secondaryButtonText && onSecondaryButtonClick && (
            <Button version="revamp-v1" variant="solid" color="secondary" onClick={onSecondaryButtonClick}>
              {secondaryButtonText}
            </Button>
          )}
          <Button version="revamp-v1" variant="solid" color="primary" onClick={onButtonClick}>
            {buttonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ErrorModal;
